const DB_NAME = "stackmap-advisor-analytics";
const DB_VERSION = 2;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("sessions")) {
        db.createObjectStore("sessions", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("events")) {
        const es = db.createObjectStore("events", { keyPath: "id", autoIncrement: true });
        es.createIndex("sessionId", "sessionId", { unique: false });
        es.createIndex("eventType", "eventType", { unique: false });
      }
      if (!db.objectStoreNames.contains("recommendations")) {
        const rs = db.createObjectStore("recommendations", { keyPath: "id", autoIncrement: true });
        rs.createIndex("sessionId", "sessionId", { unique: false });
        rs.createIndex("product", "product", { unique: false });
      }
    };
  });
  return dbPromise;
}

export interface AnalyticsSession {
  id: string;
  pagePath: string;
  openedAt: number;
  closedAt?: number;
  lastStep: number;
  completed: boolean;
  submittedForm: boolean;
  productsRecommended: string[];
  demoBooked?: boolean;
  trialStarted?: boolean;
  answers?: Record<string, unknown>;
}

export interface AnalyticsEvent {
  id?: number;
  sessionId: string;
  eventType: string;
  step?: number;
  data?: Record<string, unknown>;
  timestamp: number;
}

export interface RecommendationRecord {
  id?: number;
  sessionId: string;
  product: string;
  timestamp: number;
}

// ---- Sessions ----

export async function createSession(session: AnalyticsSession): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("sessions", "readwrite");
    const store = tx.objectStore("sessions");
    const req = store.add(session);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function updateSession(id: string, updates: Partial<AnalyticsSession>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("sessions", "readwrite");
    const store = tx.objectStore("sessions");
    const req = store.get(id);
    req.onsuccess = () => {
      const existing = req.result as AnalyticsSession | undefined;
      if (!existing) return reject(new Error("Session not found"));
      const updated = { ...existing, ...updates };
      const putReq = store.put(updated);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function getAllSessions(): Promise<AnalyticsSession[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("sessions", "readonly");
    const store = tx.objectStore("sessions");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as AnalyticsSession[]);
    req.onerror = () => reject(req.error);
  });
}

// ---- Events ----

export async function logEvent(event: Omit<AnalyticsEvent, "id">): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("events", "readwrite");
    const store = tx.objectStore("events");
    const req = store.add(event);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getAllEvents(): Promise<AnalyticsEvent[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("events", "readonly");
    const store = tx.objectStore("events");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as AnalyticsEvent[]);
    req.onerror = () => reject(req.error);
  });
}

// ---- Recommendations ----

export async function logRecommendations(recs: RecommendationRecord[]): Promise<void> {
  if (recs.length === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("recommendations", "readwrite");
    const store = tx.objectStore("recommendations");
    let completed = 0;
    for (const rec of recs) {
      const req = store.add(rec);
      req.onsuccess = () => {
        completed++;
        if (completed === recs.length) resolve();
      };
      req.onerror = () => reject(req.error);
    }
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllRecommendations(): Promise<RecommendationRecord[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("recommendations", "readonly");
    const store = tx.objectStore("recommendations");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as RecommendationRecord[]);
    req.onerror = () => reject(req.error);
  });
}

// ---- Aggregate helpers ----

export function getProductPopularity(recs: RecommendationRecord[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const r of recs) {
    map.set(r.product, (map.get(r.product) || 0) + 1);
  }
  return new Map([...map.entries()].sort((a, b) => b[1] - a[1]));
}

export function getSessionTotalQuestions(session: AnalyticsSession): number {
  const a = session.answers ?? {};
  let total = 5; // base: industry, teamSize, challenges, workMode, currentTools
  const industry = typeof a.industry === "string" ? a.industry : "";
  const workMode = typeof a.workMode === "string" ? a.workMode : "";
  if (industry === "healthcare" || industry === "manufacturing" || industry === "construction") total++;
  if (workMode === "remote" || workMode === "field") total++;
  return total;
}

export function getFunnelData(sessions: AnalyticsSession[]) {
  const steps = ["Opened", "Q1", "Q2", "Q3", "Q4", "Q5", "Q6", "Results", "Form", "Submitted"];
  const counts: Record<string, number> = {
    Opened: 0,
    Q1: 0,
    Q2: 0,
    Q3: 0,
    Q4: 0,
    Q5: 0,
    Q6: 0,
    Results: 0,
    Form: 0,
    Submitted: 0,
  };

  for (const s of sessions) {
    const totalQs = getSessionTotalQuestions(s);
    counts.Opened++;
    if (s.lastStep >= 1) counts.Q1++;
    if (s.lastStep >= 2) counts.Q2++;
    if (s.lastStep >= 3) counts.Q3++;
    if (s.lastStep >= 4) counts.Q4++;
    if (s.lastStep >= 5) counts.Q5++;
    if (s.lastStep >= 6 && totalQs >= 6) counts.Q6++;
    if (s.completed || s.lastStep >= totalQs + 1) counts.Results++;
    if (s.lastStep >= totalQs + 2) counts.Form++;
    if (s.submittedForm) counts.Submitted++;
  }

  return steps.map((label) => ({
    label,
    count: counts[label] || 0,
    percentage: counts.Opened > 0 ? Math.round(((counts[label] || 0) / counts.Opened) * 100) : 0,
  }));
}

export function getTopDropOffStep(sessions: AnalyticsSession[]): { step: number; label: string; count: number } {
  const dropOffMap = new Map<number, number>();
  for (const s of sessions) {
    if (s.completed || s.submittedForm) continue;
    const step = s.lastStep ?? 0;
    dropOffMap.set(step, (dropOffMap.get(step) || 0) + 1);
  }
  let maxStep = 0;
  let maxCount = 0;
  for (const [step, count] of dropOffMap) {
    if (count > maxCount) {
      maxCount = count;
      maxStep = step;
    }
  }
  return { step: maxStep, label: stepLabel(maxStep), count: maxCount };
}

export function stepLabel(step: number): string {
  if (step === 0) return "Opened (Intro)";
  if (step === 1) return "Q1: Industry";
  if (step === 2) return "Q2: Team Size / Compliance";
  if (step === 3) return "Q3: Team Size / Challenges";
  if (step === 4) return "Q4: Challenges / Work Mode";
  if (step === 5) return "Q5: Work Mode / Tools";
  if (step === 6) return "Q6: Branch / Tools";
  if (step === 7) return "Results";
  if (step === 8) return "Demo Form";
  return "Opened (Intro)";
}

// ---- Branching / Question Analytics ----

export interface BranchStat {
  questionId: string;
  label: string;
  shownCount: number;
  answeredCount: number;
  skipCount: number;
  skipRate: number;
  answerBreakdown: { value: string; label: string; count: number; pct: number }[];
}

const QUESTION_LABELS: Record<string, string> = {
  industry: "Industry",
  teamSize: "Team Size",
  challenges: "Challenges",
  workMode: "Work Mode",
  currentTools: "Current Tools",
  complianceNeeds: "Compliance Needs",
  locationCount: "Location Count",
};

const ANSWER_LABELS: Record<string, string> = {
  // Industry
  logistics: "Logistics / Fulfilment",
  retail: "Retail / E-commerce",
  manufacturing: "Manufacturing",
  professional: "Professional Services",
  healthcare: "Healthcare",
  construction: "Construction / Field",
  bespoke: "Bespoke / Custom",
  other: "Other",
  // Team Size
  "1-10": "1-10 people",
  "11-50": "11-50 people",
  "51-200": "51-200 people",
  "200+": "200+ people",
  // Challenges
  staff: "Staff / Productivity",
  operations: "Operations Tracking",
  reporting: "Reporting & Visibility",
  billing: "Billing / Revenue",
  performance: "Performance",
  marketing: "Marketing & Lead Gen",
  sales: "Sales Pipeline / CRM",
  workplace: "Workplace Services",
  // Work Mode
  office: "Office / On-site",
  remote: "Remote / Hybrid",
  field: "Field / Mobile",
  // Current Tools
  excel: "Excel / Spreadsheets",
  whatsapp: "WhatsApp / Messaging",
  erp: "Existing ERP",
  none: "None / Paper",
  // Compliance Needs
  "quality-audits": "Quality Audits",
  "safety-inspections": "Safety Inspections",
  "regulatory-reporting": "Regulatory Reporting",
  all: "All of the Above",
  // Location Count
  single: "Single Location",
  "2-5": "2 to 5 Locations",
  "6+": "6+ Locations",
};

export function getAvgSessionDuration(sessions: AnalyticsSession[]): number {
  const durations = sessions
    .filter((s) => s.closedAt && s.openedAt)
    .map((s) => (s.closedAt! - s.openedAt) / 1000);
  if (durations.length === 0) return 0;
  const sum = durations.reduce((a, b) => a + b, 0);
  return Math.round(sum / durations.length);
}

export function getBranchingStats(sessions: AnalyticsSession[]): {
  total: number;
  completed: number;
  avgQuestionsAnswered: number;
  branchHitRate: number;
  stats: BranchStat[];
  branchesHit: {
    complianceNeeds: number;
    locationCount: number;
  };
} {
  const total = sessions.length;
  const completed = sessions.filter((s) => s.completed).length;

  let totalQuestionsAnswered = 0;
  let complianceHits = 0;
  let locationHits = 0;

  const statMap = new Map<string, {
    shown: number;
    answered: number;
    skipped: number;
    valueCounts: Map<string, number>;
  }>();

  const allQuestionIds = ["industry", "teamSize", "challenges", "workMode", "currentTools", "complianceNeeds", "locationCount"];
  for (const qid of allQuestionIds) {
    statMap.set(qid, { shown: 0, answered: 0, skipped: 0, valueCounts: new Map() });
  }

  for (const s of sessions) {
    const answers = s.answers ?? {};
    let sessionQuestionCount = 0;

    // Industry always shown
    const industryStat = statMap.get("industry")!;
    industryStat.shown++;
    if (answers.industry !== undefined) {
      industryStat.answered++;
      sessionQuestionCount++;
      const val = String(answers.industry);
      industryStat.valueCounts.set(val, (industryStat.valueCounts.get(val) || 0) + 1);
    }

    // Team size always shown
    const teamStat = statMap.get("teamSize")!;
    teamStat.shown++;
    if (answers.teamSize !== undefined) {
      teamStat.answered++;
      sessionQuestionCount++;
      const val = String(answers.teamSize);
      teamStat.valueCounts.set(val, (teamStat.valueCounts.get(val) || 0) + 1);
    }

    // Challenges always shown
    const chalStat = statMap.get("challenges")!;
    chalStat.shown++;
    if (answers.challenges !== undefined) {
      chalStat.answered++;
      sessionQuestionCount++;
      const val = answers.challenges;
      if (Array.isArray(val)) {
        for (const v of val) {
          if (typeof v === "string") {
            chalStat.valueCounts.set(v, (chalStat.valueCounts.get(v) || 0) + 1);
          }
        }
      } else if (typeof val === "string") {
        chalStat.valueCounts.set(val, (chalStat.valueCounts.get(val) || 0) + 1);
      }
    }

    // Work mode always shown
    const modeStat = statMap.get("workMode")!;
    modeStat.shown++;
    if (answers.workMode !== undefined) {
      modeStat.answered++;
      sessionQuestionCount++;
      const val = String(answers.workMode);
      modeStat.valueCounts.set(val, (modeStat.valueCounts.get(val) || 0) + 1);
    }

    // Compliance branch — shown if industry triggers it
    const compStat = statMap.get("complianceNeeds")!;
    const industry = typeof answers.industry === "string" ? answers.industry : "";
    if (industry === "healthcare" || industry === "manufacturing" || industry === "construction") {
      compStat.shown++;
      complianceHits++;
      if (answers.complianceNeeds !== undefined) {
        compStat.answered++;
        sessionQuestionCount++;
        const val = answers.complianceNeeds;
        if (Array.isArray(val)) {
          for (const v of val) {
            if (typeof v === "string") {
              compStat.valueCounts.set(v, (compStat.valueCounts.get(v) || 0) + 1);
            }
          }
        } else if (typeof val === "string") {
          compStat.valueCounts.set(val, (compStat.valueCounts.get(val) || 0) + 1);
        }
      } else {
        compStat.skipped++;
      }
    }

    // Location branch — shown if work mode triggers it
    const locStat = statMap.get("locationCount")!;
    const workMode = typeof answers.workMode === "string" ? answers.workMode : "";
    if (workMode === "remote" || workMode === "field") {
      locStat.shown++;
      locationHits++;
      if (answers.locationCount !== undefined) {
        locStat.answered++;
        sessionQuestionCount++;
        const val = String(answers.locationCount);
        locStat.valueCounts.set(val, (locStat.valueCounts.get(val) || 0) + 1);
      } else {
        locStat.skipped++;
      }
    }

    // Current tools always shown
    const toolsStat = statMap.get("currentTools")!;
    toolsStat.shown++;
    if (answers.currentTools !== undefined) {
      toolsStat.answered++;
      sessionQuestionCount++;
      const val = answers.currentTools;
      if (Array.isArray(val)) {
        for (const v of val) {
          if (typeof v === "string") {
            toolsStat.valueCounts.set(v, (toolsStat.valueCounts.get(v) || 0) + 1);
          }
        }
      } else if (typeof val === "string") {
        toolsStat.valueCounts.set(val, (toolsStat.valueCounts.get(val) || 0) + 1);
      }
    } else {
      // "none" not selected = skippable but empty means skipped
      // However, we can't know if they explicitly skipped vs just didn't select
      // We'll count empty as "answered" since it's skippable
      toolsStat.answered++;
      sessionQuestionCount++;
    }

    totalQuestionsAnswered += sessionQuestionCount;
  }

  const avgQuestionsAnswered = total > 0 ? Math.round((totalQuestionsAnswered / total) * 10) / 10 : 0;
  const branchHitRate = total > 0 ? Math.round(((complianceHits + locationHits) / total) * 100) : 0;

  const stats: BranchStat[] = allQuestionIds.map((qid) => {
    const st = statMap.get(qid)!;
    const totalAnswersForPct = Array.from(st.valueCounts.values()).reduce((a, b) => a + b, 0);
    const answerBreakdown = Array.from(st.valueCounts.entries())
      .map(([value, count]) => ({
        value,
        label: ANSWER_LABELS[value] || value,
        count,
        pct: totalAnswersForPct > 0 ? Math.round((count / totalAnswersForPct) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      questionId: qid,
      label: QUESTION_LABELS[qid] || qid,
      shownCount: st.shown,
      answeredCount: st.answered,
      skipCount: st.skipped,
      skipRate: st.shown > 0 ? Math.round((st.skipped / st.shown) * 100) : 0,
      answerBreakdown,
    };
  });

  return {
    total,
    completed,
    avgQuestionsAnswered,
    branchHitRate,
    stats,
    branchesHit: {
      complianceNeeds: complianceHits,
      locationCount: locationHits,
    },
  };
}



export function exportToCSV(sessions: AnalyticsSession[]): string {
  const headers = [
    "Session ID",
    "Page Path",
    "Opened At",
    "Closed At",
    "Last Step",
    "Completed",
    "Form Submitted",
    "Demo Booked",
    "Trial Started",
    "Products Recommended",
    "Industry",
    "Team Size",
    "Challenges",
    "Work Mode",
    "Current Tools",
    "Compliance Needs",
    "Location Count",
    "Questions Shown",
  ];
  const fmt = (v: unknown): string => {
    if (Array.isArray(v)) return v.join("; ");
    if (v === undefined || v === null) return "";
    return String(v);
  };
  const rows = sessions.map((s) => [
    s.id,
    s.pagePath,
    new Date(s.openedAt).toISOString(),
    s.closedAt ? new Date(s.closedAt).toISOString() : "",
    s.lastStep,
    s.completed ? "Yes" : "No",
    s.submittedForm ? "Yes" : "No",
    s.demoBooked ? "Yes" : "No",
    s.trialStarted ? "Yes" : "No",
    (s.productsRecommended || []).join("; "),
    fmt(s.answers?.industry),
    fmt(s.answers?.teamSize),
    fmt(s.answers?.challenges),
    fmt(s.answers?.workMode),
    fmt(s.answers?.currentTools),
    fmt(s.answers?.complianceNeeds),
    fmt(s.answers?.locationCount),
  ]);
  return [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
}

// ---- Clear ----

export async function clearAllAnalytics(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(["sessions", "events", "recommendations"], "readwrite");
    tx.objectStore("sessions").clear();
    tx.objectStore("events").clear();
    tx.objectStore("recommendations").clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}